export interface TimelineableRecord {
  id: string
  timelineYearsRange: [number, number]
}

type AggregateResult = Map<number, readonly string[]>

/**
 * Groups records by year, returning a sorted map where each year contains
 * the IDs of records that are active during that year. Gap years (with no
 * active records) are always included.
 *
 * @param records - Array of TimelineableRecord instances to group
 * @param endYear - Optional end year to extend the map beyond the last record
 * @returns Sorted Map with year as key and array of record IDs as value
 */
const aggregateRecordsByYear = (
  records: readonly TimelineableRecord[],
  endYear?: number
): AggregateResult => {
  const yearMap = new Map<number, Set<string>>()

  for (const record of records) {
    const [start, end] = record.timelineYearsRange
    for (let year = start; year <= end; year++) {
      if (!yearMap.has(year)) yearMap.set(year, new Set())
      yearMap.get(year)!.add(record.id)
    }
  }

  const sortedYears = [...yearMap.keys()].sort((a, b) => a - b)
  if (sortedYears.length === 0) return new Map()

  const result: AggregateResult = new Map()
  const lastRecordYear = sortedYears.at(-1)!
  const lastYear = Math.max(lastRecordYear, endYear ?? lastRecordYear)

  for (let year = sortedYears[0]; year <= lastYear; year++) {
    const ids = yearMap.get(year)
    result.set(year, ids ? [...ids] : [])
  }

  return result
}

export default aggregateRecordsByYear
